import './Datepicker.css';

import React from 'react';
import classnames from 'classnames';
import { Matcher } from 'react-day-picker';
import { Icon } from '../../Icon/Icon';
import { formatDate, formatDateTime } from '../../../util/util';
import { Hourpicker } from './Hourpicker';
import { t } from '../../../locales/translate';
import { Tooltip } from '../../Tooltip/Tooltip';
import * as PopperJS from 'popper.js';
import { FormField } from '../../../model/form';
import { FormTextField } from '../TextField/FormTextField';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';
import { StyledDayPicker } from '../styled_day_picker/styled_day_picker';

interface Props extends FormField<Date> {
  position?: PopperJS.Placement;
  hourpicker?: boolean;
  disabledDays?: Matcher | Matcher[];
  hideClearButton?: boolean;
}

export function FormDatepicker({
  name,
  value,
  label,
  required,
  disabled,
  description,
  className,
  style,
  position = 'bottom-start',
  hourpicker,
  disabledDays,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  touched,
  valid,
  focused,
  icon,
  errors,
  hideClearButton = false,
  inputId,
}: Props): JSX.Element {
  const displayErrors = touched && !valid;

  function handleOnChange(value?: Date | undefined): void {
    if (!disabled) {
      if (value) {
        value.setHours(12, 0, 0, 0);
      }
      onChange?.(value);
    }
  }

  return (
    <div
      className={classnames('datepicker', className, {
        'datepicker--disabled': disabled,
      })}
      style={style}
    >
      <Tooltip
        position={position}
        mode="click"
        gap="none"
        arrow={false}
        rounded={false}
        open={focused}
        onChange={(open: boolean) => {
          (open ? onFocus : onBlur)?.();
        }}
        dontCloseOnParentClick
        reference={
          <div id={name} className="datepicker__input">
            <FormTextField
              inputId={inputId}
              displayOptional={!required}
              value={hourpicker ? formatDateTime(value) : formatDate(value)}
              placeholder={placeholder}
              disabled={disabled}
              readOnly
              required={required}
              icon={icon}
              label={label}
              actions={
                value &&
                !hideClearButton && (
                  <span onClick={() => handleOnChange(undefined)}>
                    <Icon name="remove-circle--sm" />
                  </span>
                )
              }
            />
          </div>
        }
      >
        <StyledDayPicker
          selected={value || undefined}
          mode="single"
          onSelect={handleOnChange}
          numberOfMonths={1}
          disabled={disabledDays}
        />
        {hourpicker && (
          <div className="datepicker__hourpicker">
            <Hourpicker date={value ?? undefined} onChange={onChange} />
          </div>
        )}
      </Tooltip>
      <div className="datepicker__description">
        {description && <div className="input__description">{description}</div>}
        <Collapse open={displayErrors}>
          {errors?.map((error) => {
            return (
              <div className="input__error-message" key={error}>
                {t.errors[error] || error}
              </div>
            );
          })}
        </Collapse>
      </div>
    </div>
  );
}
